// provisioning.ts — señal de "cuenta recién creada" para romper el ping-pong
// dashboard↔signup mientras el custom claim `breweryId` termina de propagar al
// token del cliente (ventana de ~1-1.5 min tras registrarse con Google).
//
// El servidor (registerBrewery) DEVUELVE el breweryId apenas crea la cervecería,
// así que el cliente ya SABE que la cuenta existe aunque el token viejo todavía
// no traiga el claim. Guardamos esa certeza acá para que AuthGuard, en vez de
// rebotar a /signup, espere quieto en "Cargando…" hasta que el claim llegue.

const KEY = "cc:provisioning";
const MAX_AGE_MS = 5 * 60 * 1000; // la señal vale ~5 min; después se ignora (evita estados zombie)

export type ProvisioningFlag = { breweryId: string; ts: number };

/** Marca que este navegador acaba de crear una cervecería (breweryId ya conocido). */
export function markProvisioning(breweryId: string): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ breweryId, ts: Date.now() }));
  } catch {
    /* sessionStorage puede fallar en modo privado/SSR — no es crítico */
  }
}

/** Devuelve la señal si está vigente (< 5 min), o null. */
export function readProvisioning(): ProvisioningFlag | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ProvisioningFlag;
    if (!parsed?.breweryId || typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > MAX_AGE_MS) {
      clearProvisioning();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Limpia la señal (claim ya presente, o abandono). */
export function clearProvisioning(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}

// ── Diagnóstico ───────────────────────────────────────────────────────────────
// Logger de diagnóstico del flujo de auth/registro. Deja migas con timestamp para
// poder LEER en la consola qué guardia disparó y si el claim estaba o no, sin
// tener que adivinar. Prefijo único `[CC-AUTH]` para filtrar fácil en DevTools.
export function authDiag(scope: string, msg: string, extra?: Record<string, unknown>): void {
  try {
    const t = new Date().toISOString().slice(11, 23); // hh:mm:ss.mmm
    console.info(`[CC-AUTH ${t}] ${scope}: ${msg}`, extra ?? "");
  } catch {
    /* noop */
  }
}
